import React, { useEffect, useState, Component } from 'react';

import { BrowserRouter } from 'react-router-dom';

import { ThemeProvider, CssBaseline } from '@mui/material';

import AuthContext from './Context/AuthContext';
import { USER_TYPES } from './Constant/Enums';

import theme from './Components/defaultTheme';
import { PageError } from './Components/ErrorStates';

import { observeFirebaseAuth } from './lib/firebaseClients';
import { getStoredSession, LOGOUT } from './lib/Token';

import Routes from './Routes';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return (
        <ThemeProvider theme={theme}>
          <BrowserRouter>
            <PageError
              title='Something went wrong'
              message='An unexpected error stopped the page. Reload to start over.'
              onRetry={() => window.location.reload()}
              retryLabel='Reload page'
            />
          </BrowserRouter>
        </ThemeProvider>
      );
    }
    return this.props.children;
  }
}

function App() {
  const [user, setUser] = useState(() => getStoredSession());
  const [isAuthReady, setIsAuthReady] = useState(() => !getStoredSession()?.type);
  const isAdminLogged = user?.type === USER_TYPES.ADMIN;

  useEffect(() => {
    const storedSession = getStoredSession();

    // No session in THIS tab → nothing to restore, and (crucially) we don't
    // touch any Firebase app. Only observe the one app matching this tab's
    // session type, so a tab never initialises another identity's Firebase
    // instance — that cross-app interference is what signed other tabs out.
    if (!storedSession?.type) {
      setIsAuthReady(true);
      return undefined;
    }

    const unsubscribe = observeFirebaseAuth(storedSession.type, firebaseUser => {
      if (!firebaseUser) {
        LOGOUT();
        setUser(null);
        setIsAuthReady(true);
        return;
      }

      setUser(getStoredSession());
      setIsAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthContext.Provider
        value={{
          user,
          setUser,
          isAdminLogged,
          setIsAdminLogged: () => {
            if (!isAdminLogged) return;
            LOGOUT();
            setUser(null);
          },
        }}
      >
        {isAuthReady ? <Routes /> : null}
      </AuthContext.Provider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
